import { useEffect, useState } from "react";
import {
  Sparkles, Search, Store, Users, Building2, Wallet, Briefcase, BarChart3,
  Calendar, Flame, Star, Loader2, CheckCircle2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/flows/marketplace`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CAT_ICON = {
  "All": Sparkles, "Sales": Store, "Field Force": Users, "Operations": Building2,
  "Finance": Wallet, "HR": Briefcase, "Intelligence": BarChart3, "Scheduled": Calendar,
};
const CAT_TONE = {
  "Sales": "bg-rose-50 text-rose-700 border-rose-200",
  "Field Force": "bg-sky-50 text-sky-700 border-sky-200",
  "Operations": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Finance": "bg-amber-50 text-amber-700 border-amber-200",
  "HR": "bg-violet-50 text-violet-700 border-violet-200",
  "Intelligence": "bg-pink-50 text-pink-700 border-pink-200",
  "Scheduled": "bg-slate-100 text-slate-700 border-slate-200",
};

export default function FlowMarketplacePage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState({});
  const navigate = useNavigate();

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (active !== "All") params.set("category", active);
    if (q) params.set("q", q);
    fetch(`${API}?${params}`, { headers: auth() })
      .then(r => r.json()).then(j => setItems(j.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetch(`${API}/categories`, { headers: auth() }).then(r => r.json()).then(j => setCats(j.data || []));
  }, []);

  useEffect(() => {
    const id = setTimeout(reload, 250);
    return () => clearTimeout(id);
  }, [active, q]);

  const install = async (tpl) => {
    setInstalling((s) => ({ ...s, [tpl.id]: true }));
    try {
      const r = await fetch(`${API}/install/${tpl.id}`, { method: "POST", headers: auth() });
      const j = await r.json();
      if (j.success) {
        toast.success(`Installed: ${tpl.title}`);
        navigate(`/flows`);
      } else toast.error("Install failed");
    } finally {
      setInstalling((s) => ({ ...s, [tpl.id]: false }));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="flow-marketplace-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Sparkles className="text-amber-500" size={28} /> Flow Marketplace
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Curated, ready-to-install automations. Pick a card, click Install, you're live.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search by name, tag, or pain point…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]"
            data-testid="marketplace-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="marketplace-category-bar">
        {cats.map((c) => {
          const Icon = CAT_ICON[c.name] || Sparkles;
          const isActive = active === c.name;
          return (
            <button
              key={c.name}
              onClick={() => setActive(c.name)}
              data-testid={`marketplace-cat-${c.name.replace(/\s+/g, '-')}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                isActive ? "bg-[#8B1A1A] text-white border-[#8B1A1A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              <Icon size={12} /> {c.name} <span className="opacity-60">· {c.count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          No templates match your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-4 hover:shadow-lg transition dark:bg-slate-800" data-testid={`marketplace-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline" className={`text-xs ${CAT_TONE[t.category] || ""}`}>{t.category}</Badge>
                <div className="flex gap-1">
                  {t.popular && <Badge className="text-xs bg-amber-500 text-white border-0"><Flame size={10} className="mr-0.5" /> Popular</Badge>}
                  {t.is_new && <Badge className="text-xs bg-emerald-500 text-white border-0"><Star size={10} className="mr-0.5" /> New</Badge>}
                </div>
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-base mb-1">{t.title}</h3>
              <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.tagline}</p>
              <div className="text-[11px] text-slate-600 dark:text-slate-300 space-y-1 mb-3 min-h-[58px]">
                <div><span className="font-semibold text-red-600">Problem:</span> {t.problem}</div>
                <div><span className="font-semibold text-green-600">Solves:</span> {t.outcome}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-slate-400">
                  {t.install_count > 0 ? `${t.install_count} installs this month` : "Be the first to install"}
                </div>
                <Button
                  size="sm"
                  onClick={() => install(t)}
                  disabled={installing[t.id]}
                  data-testid={`install-${t.id}`}
                  className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white"
                >
                  {installing[t.id] ? <><Loader2 size={12} className="animate-spin mr-1" /> Installing</> : <><CheckCircle2 size={12} className="mr-1" /> Install</>}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
